import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  magicMockApiV1UnifiedProfilesPost,
  useGenerateProfileTokenApiV1UnifiedGenerateProfileTokenPost,
  studentsControllerGetEnrolledPrograms,
  LiveclassStudent,
  getAllMappings,
} from "@oneapp/data";
import {
  ROUTESWEB,
  findCountryMappingByIsdCode,
  findCountryMappingByTimezoneId,
  getCountryTimezoneOptions,
} from "@repo/utils";
import {
  AnalyticsEvents,
  getProductVertical,
  setUserProfile,
  trackAuthSuccess,
  trackLoginSuccess,
  emitPostAuthMarketingLsq,
} from "@repo/analytics";
import { analyticsManager } from "../../../lib/setupAnalytics";
import {
  syncAuthIdentityToAnalytics,
  trackAuthLoginSuccess,
} from "../../../lib/authAnalytics";

import {
  useAuthStore,
  type EnrolledProgram,
  type ProfilesData,
  type CountryDetails,
} from "../../../store/useAuthStore";
import { buildLiveclassStudentSyncPayload } from "../../../lib/liveclassStudentSyncPayload";

import type { UnifiedAccount } from "./accountCard";
import { useprofileUpdater } from "../../profile/api";

export const getGatewayHeaders = (authorization: string) => {
  const token = authorization.replace(/^Bearer\s+/i, "").trim();
  return {
    Authorization: authorization,
    accesstoken: token,
    platform: "web",
    "product-id": import.meta.env.VITE_PRODUCT_ID ?? "300",
    "tenant-id": import.meta.env.VITE_TENANT_ID ?? "1",
    "ot-tenant": "infinitylearn",
    "x-tenant": "infinitylearn",
    "x-platform": "web",
    "x-product": "1",
  };
};

type ProfilesApiResponse = {
  access_token?: string;
  refresh_token?: string;
  unified_account_details?: {
    unified_account_id?: string;
    profiles?: Array<{ uam_id?: number }>;
    user_account_details?: Record<string, unknown>;
    [key: string]: unknown;
  };
};

export type UnifiedAccountSelectionFlowOptions = {
  phoneNumber: string;
  isd_code: string;
  whatsapp_consent: boolean;
  /** Bearer used for the initial profiles POST (OTP unified token or session). */
  getProfilesBearerToken: () => string | undefined;
};

export function useUnifiedAccountSelectionFlow({
  phoneNumber,
  isd_code,
  whatsapp_consent,
  getProfilesBearerToken,
}: UnifiedAccountSelectionFlowOptions) {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const clearTransientTokens = useAuthStore((s) => s.clearTransientTokens);
  const setUnifiedAccountDetails = useAuthStore(
    (s) => s.setUnifiedAccountDetails,
  );
  const setProfilesData = useAuthStore((s) => s.setProfilesData);
  const setEnrolledPrograms = useAuthStore((s) => s.setEnrolledPrograms);
  const setTransientTokens = useAuthStore((s) => s.setTransientTokens);
  const setLoginMethod = useAuthStore((s) => s.setLoginMethod);
  const generateProfileTokenMutation =
    useGenerateProfileTokenApiV1UnifiedGenerateProfileTokenPost({
      request: {
        clientName: "learnGateway",
      },
    });
  const { profileUpdater } = useprofileUpdater();

  const [isProceeding, setIsProceeding] = useState(false);
  const [showContactSupportModal, setShowContactSupportModal] = useState(false);
  const pendingIlSwitchUamIdRef = useRef<string | null>(null);

  const handleContactSupportConfirm = useCallback(async () => {
    const ilUamId = pendingIlSwitchUamIdRef.current;
    pendingIlSwitchUamIdRef.current = null;
    if (!ilUamId) return;

    setLoginMethod("phone_otp");
    try {
      await profileUpdater({
        uam_id: ilUamId,
        is_default_profile: true,
      });
    } catch (err) {
      console.warn(
        "[Auth] Failed to switch to IL profile after Contact Support confirm:",
        err,
      );
    }
    setShowContactSupportModal(false);
  }, [profileUpdater, setLoginMethod]);

  const proceedWithAccount = useCallback(
    async (account: UnifiedAccount | null): Promise<boolean> => {
      const token = getProfilesBearerToken() ?? "";
      if (!token || !account?.unified_account_id) return false;

      setIsProceeding(true);
      try {
        const response = (await magicMockApiV1UnifiedProfilesPost(
          {
            unified_account_id: account.unified_account_id,
          } as unknown as Parameters<
            typeof magicMockApiV1UnifiedProfilesPost
          >[0],
          {
            clientName: "learnGateway",
            headers: getGatewayHeaders(`Bearer ${token}`),
          },
        )) as ProfilesApiResponse;

        const profilesPayload = response as ProfilesData;

        const profiles =
          profilesPayload?.unified_account_details?.profiles ?? [];

        const defaultProfile =
          profiles.find((profile) => profile.is_default_profile) ??
          profiles.find((profile) => profile.vertical_id === 1) ??
          profiles[0];

        const normalizedProfilesPayload: ProfilesData = {
          ...profilesPayload,
          unified_account_details: {
            ...profilesPayload?.unified_account_details,
            profiles:
              defaultProfile && profiles.length > 0
                ? [
                    defaultProfile,
                    ...profiles.filter((profile) => profile !== defaultProfile),
                  ]
                : profiles,
          },
        };

        const ac2token = response?.access_token;
        const details = response?.unified_account_details;
        const uam_id = defaultProfile?.uam_id;

        if (!uam_id) {
          console.warn("Profile information missing for this account.");
          return false;
        }
        if (!ac2token) {
          console.warn("Profile session could not be established.");
          return false;
        }

        const genData = await generateProfileTokenMutation.mutateAsync({
          data: {
            unified_account_id: account.unified_account_id,
            uam_id,
          },
          request: { headers: getGatewayHeaders(`Bearer ${ac2token}`) },
        });

        const genDataTyped = genData as {
          access_token?: string;
          refresh_token?: string;
        };

        if (!genDataTyped.access_token) {
          console.warn("Failed to complete login.");
          return false;
        }

        try {
          const userDetails =
            normalizedProfilesPayload.unified_account_details
              ?.user_account_details;
          const mappings = await getAllMappings(
            { resource_type: "country" },
            { clientName: "masterData" },
          );
          const timezoneId = userDetails?.timezone_id;
          const savedCountry = findCountryMappingByTimezoneId(
            mappings,
            timezoneId ?? null,
          );
          const country =
            savedCountry ??
            findCountryMappingByIsdCode(
              mappings,
              userDetails?.isd_code || isd_code,
            );
          const options = getCountryTimezoneOptions(country);
          const selectedTimezoneId =
            savedCountry && timezoneId != null
              ? timezoneId
              : options.length === 1
                ? options[0].id
                : undefined;
          // Country headers are required even before a timezone is selected.
          const countryDetails = (country as unknown as CountryDetails) ?? null;
          useAuthStore
            .getState()
            .setCountryDetails(countryDetails, selectedTimezoneId);
        } catch (error) {
          useAuthStore.getState().setCountryDetails(null);
          console.warn(
            "[Auth] Failed to restore switched account timezone",
            error,
          );
        }
        setProfilesData(normalizedProfilesPayload);

        if (details) {
          setUnifiedAccountDetails(details);
        } else {
          setUnifiedAccountDetails(null);
        }

        const uad = details?.user_account_details;
        const firstName = (uad?.first_name as string) ?? "";
        const lastName = (uad?.last_name as string) ?? "";
        const name =
          `${firstName} ${lastName}`.trim() ||
          `${account.first_name} ${account.last_name}`.trim();
        const roleName =
          (uad?.user_type as { name?: string })?.name ||
          (uad?.role as { role_name?: string })?.role_name;

        const uadGrade = details?.user_account_details as
          | { grade?: { gradeId?: number } }
          | undefined;
        const gradeId = uadGrade?.grade?.gradeId ?? account.grade ?? 0;
        const firstProfileWithExams = profiles?.[0] as
          | { exams?: Array<{ examId?: number }> }
          | undefined;
        const examsParam = defaultProfile?.exams
          ?.map((e) => e.examId)
          .filter((id): id is number => id != null)
          .join(",");
        const accessToken = genDataTyped.access_token;
        const enrolledProgramsHeaders = {
          Authorization: `Bearer ${accessToken}`,
          accesstoken: accessToken,
          "x-platform": "web",
          "x-tenant": "infinitylearn",
        };
        try {
          const enrolledResponse = (await studentsControllerGetEnrolledPrograms(
            {
              grade: gradeId,
              processEnrollment: true,
              ...(examsParam ? { exams: examsParam } : {}),
            },
            { clientName: "oneApp", headers: enrolledProgramsHeaders },
          )) as { data?: { programs?: EnrolledProgram[] } } | undefined;
          const programs = enrolledResponse?.data?.programs;

          // call event login

          // ✅ Prepare values safely
          // const userId = String(details?.unified_account_id ?? "");
          const examsList =
            firstProfileWithExams?.exams
              ?.map((e) => e.examId)
              .filter(Boolean)
              .join(", ") ?? "";

          const programName = programs?.[0]?.program_name ?? "";

          // call webenagge event---------
          const verticalId = defaultProfile?.vertical_id;

          // 1. Set WebEngage user
          setUserProfile();

          // 2. Track LOGIN SUCCESS
          trackLoginSuccess({
            event_datetime: new Date(),
            uam_id: String(uam_id),
            first_name: firstName,
            last_name: lastName,
            offering: examsList,
            program: programName,
            grade: gradeId,
            product_vertical: getProductVertical(verticalId),
            product_source: "Student Portal",
            product_tenant_id: import.meta.env.VITE_TENANT_ID ?? "1",
            page_url: window.location.href,
          });

          // 3. Track AUTH SUCCESS
          trackAuthSuccess({
            event_datetime: new Date(),
            uam_id: String(uam_id),
            first_name: firstName,
            last_name: lastName,
            offering: examsList,
            program: programName,
            grade: gradeId,
            product_vertical: getProductVertical(verticalId),
            product_source: "Student Portal",
            product_tenant_id: import.meta.env.VITE_TENANT_ID ?? "1",
            auth_type: "Login",
            type_of_user: "Existing",
          });
          if (programs && Array.isArray(programs)) {
            setEnrolledPrograms(programs);
          }
          if (!programs || !Array.isArray(programs) || programs.length === 0) {
            analyticsManager.track(AnalyticsEvents.SIGNUP_FLOW_REDIRECTED, 1, {
              reason: "no_enrollment",
              login_method: "phone_otp",
            });
            setTransientTokens(
              genDataTyped.access_token,
              genDataTyped.refresh_token,
            );
            if (verticalId === 1) {
              navigate(`/${ROUTESWEB.AUTH.SIGNUP}`, {
                state: {
                  phoneNumber,
                  isd_code: isd_code,
                  whatsapp_consent: whatsapp_consent,
                  isUpdateFlow: true,
                },
              });
            } else {
              const ilProfile = profiles.find((p) => p.vertical_id === 1);
              pendingIlSwitchUamIdRef.current =
                ilProfile?.uam_id != null ? String(ilProfile.uam_id) : null;
              setShowContactSupportModal(true);
            }
            return true;
          }
        } catch (enrolledErr) {
          console.warn(
            "[Auth] Enrolled programs call failed (MultipleAccounts):",
            enrolledErr,
          );
        }
        try {
          const payload = buildLiveclassStudentSyncPayload();
          await LiveclassStudent.create(
            payload
              ? { clientName: "liveclass", data: payload }
              : { clientName: "liveclass" },
          );
        } catch (syncErr) {
          console.warn(
            "[Auth] Students sync call failed (MultipleAccounts):",
            syncErr,
          );
        }
        setLoginMethod("phone_otp");
        login(
          genDataTyped.access_token,
          {
            id: details?.unified_account_id ?? account.unified_account_id,
            name: name || "User",
            role: roleName?.toLowerCase() === "guest" ? "guest" : "student",
          },
          genDataTyped.refresh_token,
          true,
          { skipAinaIntroPrime: true },
        );

        syncAuthIdentityToAnalytics(profilesPayload);
        trackAuthLoginSuccess("phone_otp", {
          account_selection: true,
        });

        const profilesForMarketing = profiles as
          | Array<{ vertical_id?: number; exams?: Array<{ name?: string }> }>
          | undefined;
        const ilProfile = profilesForMarketing?.find(
          (p) => p.vertical_id === 1,
        );
        const examName =
          (ilProfile?.exams?.length ?? 0 > 1)
            ? ilProfile?.exams
                ?.map((e) => e.name)
                .filter(Boolean)
                .join(",") || undefined
            : (ilProfile?.exams?.[0]?.name ?? undefined);

        const studentNameForMarketing =
          [uad?.first_name, uad?.last_name].filter(Boolean).join(" ").trim() ||
          `${firstName} ${lastName}`.trim() ||
          undefined;
        const resGrade = uad?.grade as { gradeId?: number } | null | undefined;
        const gradeForMarketing = resGrade?.gradeId ?? account.grade;

        const pageUrl =
          localStorage.getItem("web_deeplink_url") ?? window.location.href;

        emitPostAuthMarketingLsq({
          grade: gradeForMarketing,
          studentName: studentNameForMarketing,
          examName: examName as string | null | undefined,
          productVertical: ilProfile?.vertical_id?.toString(),
          academicGrade: resGrade?.gradeId,
          userEmail:
            typeof uad?.email === "string" ? uad.email.trim() : undefined,
          leadPageContext: { pageUrl },
          variant: "web",
        });

        clearTransientTokens();
        navigate(`/${ROUTESWEB.APP.DASHBOARD}`, {
          replace: true,
          state: { dashboardRefresh: Date.now() },
        });
        return true;
      } catch (err) {
        console.warn("Profiles / generate profile token failed", err);
        return false;
      } finally {
        setIsProceeding(false);
      }
    },
    [
      getProfilesBearerToken,
      login,
      clearTransientTokens,
      setUnifiedAccountDetails,
      setProfilesData,
      setEnrolledPrograms,
      setTransientTokens,
      setLoginMethod,
      generateProfileTokenMutation,
      navigate,
      phoneNumber,
      isd_code,
      whatsapp_consent,
    ],
  );

  return {
    proceedWithAccount,
    isProceeding,
    showContactSupportModal,
    setShowContactSupportModal,
    handleContactSupportConfirm,
  };
}
